import { DOCUMENT, inject, Injectable, Injector } from "@angular/core";
import {
  ITrackingErrorSchema,
  ITrackingHitSchema,
  ITrackingPageSchema,
  ITrackingSection,
  TrackingLayer
} from "../../types/trackingServices";
import { AirlineService } from "./airline.service";
import { LanguagesService } from "./languages.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { IUserProfile } from "app/types/user";
import { trackingSectionsMap } from "./tracking.data";
import { AuthService } from "./auth.service";
import Appcues from "@appcues/web-sdk";
import loadEnvironmentVariables from "../configs/env-conf-loader";
import { Observable, startWith, map } from "rxjs";
const environment = loadEnvironmentVariables();

@Injectable({ providedIn: "root" })
export class TrackingService {

  private readonly _injector = inject(Injector);
  private readonly _airlineService = inject(AirlineService);
  private readonly _languagesService = inject(LanguagesService);
  private readonly _document = inject(DOCUMENT);
  private readonly _dataLayer: Array<TrackingLayer> = (this._document.defaultView as any).dataLayer;

  private _userConsentToTrack = false;
  private _userId: string = "";
  private _userType: string = "";
  private _pageName: string = "";
  private _pageUrl: string = "";
  private _pageUri: string = "";
  private _pagePreviousName: string = "";
  private _pagePreviousUrl: string = "";
  private _cmsProvider: string = "";
  private _cmsLanguageIsoCode: string = "";
  private _cmsSection: string = "";
  private _cmsSubSection: string = "";

  private readonly _environment = environment.name === "Production" ? "PRODUCTION"
    : environment.name === "Prepro" ? "PREPRODUCTION" : "DEVELOPMENT";

  private get _pageTrackingSchema(): ITrackingPageSchema {
    return {
      page: {
        name: this._pageName,
        url: this._pageUrl,
        uri: this._pageUri,
        fullURL: this._pageUrl,
        hostname: this._document.defaultView.location.host,
        previousPageName: this._pagePreviousName,
        previousPageURL: this._pagePreviousUrl
      },
      cms: {
        userType: this._userType,
        provider: this._cmsProvider,
        languageIsoCode: this._cmsLanguageIsoCode,
        section: this._cmsSection,
        subSection: this._cmsSubSection
      },
      user: {
        id: this._userId
      },
      extraInfo: {
        timestamp: new Date().toISOString(),
        environment: this._environment,
      }
    }
  }

  constructor() {
    Appcues.setup("89465");
    this._configureConsentChangeObservable()
      .pipe(takeUntilDestroyed())
      .subscribe((consent) => {
        this._userConsentToTrack = consent;
        this._identifyUserForAppcues();
      });
    this._airlineService.currentAirlineChange
      .pipe(takeUntilDestroyed())
      .subscribe(airline => this._cmsProvider = airline?.code?.toUpperCase() || 'n/a');
    this._languagesService.currentLanguageChange
      .pipe(takeUntilDestroyed())
      .subscribe(language => this._cmsLanguageIsoCode = language?.short || "n/a");
  }

  private _configureConsentChangeObservable(): Observable<boolean> {
    return new Observable<string>(
      subscriber => (this._document.defaultView as any).pwcAnalyticsConsentGlobal.onConsentChange((value) => subscriber.next(value))
    )
      .pipe(
        startWith((this._document.defaultView as any).pwcAnalyticsConsentGlobal.getTrackingConsent()),
        map(consentValue => consentValue === "true" ? true : false)
      );
  }

  private _identifyUserForAppcues(): void {
    if (!this._userConsentToTrack) return Appcues.reset();
    const authService = this._injector.get(AuthService);
    const profile = authService.getUserProfileSync();
    if (!profile) return;
    const userAirline = authService.getUserAirlineSync();
    Appcues.identify(profile.userId, {
      role: profile.role,
      fullName: profile.fullName,
      tenant: userAirline?.code,
      standardAirtrfx: userAirline?.standard_airTRFX
    });
  }

  /**
   * Sets the value of the page data, used in the onFinish hook to collet data about the state.
   */
  public setPageData(
    component: string,
    name: string,
    absUrl: string,
    uri: string,
    previousPageName: string,
    previousPageUri: string,
    section: string,
    subSection: string
  ) {
    const parsed = this._formatSectionSubSectionName(component);
    this._pageName = name;
    this._pageUrl = absUrl;
    this._pageUri = uri;
    this._pagePreviousName = previousPageName;
    this._pagePreviousUrl = previousPageUri;
    this._cmsSection = section || parsed.section;
    this._cmsSubSection = subSection || parsed.sub;
  }

  private _formatSectionSubSectionName(componentName: string): ITrackingSection {
    const item = trackingSectionsMap.get(componentName.toLowerCase());
    return item ? item : { section: componentName, sub: componentName };
  }

  public setAuthData(profile: IUserProfile) {
    this._userId = profile.userId;
    this._userType = profile.platformUser ? 'ROOT' : 'ADMIN';
  }

  public privacySettings() {
    (this._document.defaultView as any).pwcAnalyticsConsentGlobal.showSettingsDialog();
  }

  /**
   * Pushes a pageview event to datalayer
   */
  public doTracking(pageName?: string): void {
    const schema = this._pageTrackingSchema;
    if (pageName) schema.page.name = pageName;
    this._dataLayer.push({ ...schema, event: "Pageview" });
  }

  public trackLogIn(profile: IUserProfile): void {
    this.setAuthData(profile);
    const schema = this._pageTrackingSchema;
    this._dataLayer.push({ ...schema, event: "Log_in" });
  }

  public trackError(status: number, url: string): void
  public trackError(error: ITrackingErrorSchema): void
  public trackError(error: number | ITrackingErrorSchema, url?: string): void {
    const Layer: ITrackingErrorSchema = typeof error == "number"
      ? { errorType: error, errorURL: url }
      : error;
    this._dataLayer.push({ ...Layer, event: "error" });
  }

  public trackAppcuesPage() {
    if (!this._userConsentToTrack) return;
    Appcues.page();
  }

  /**
   * Pushes a hit event to datalayer
   * @param action - Name of action
   * @param button - Text of button
   * @param name - Name of item affected
   * @param code - Pagetype code.
   * @param extraParams - Extra params to be set.
   */
  public trackHit(action: string, button: string, name?: string, code?: string, extraParams?: { [key: string]: any }): void
  public trackHit(schema: ITrackingHitSchema): void
  public trackHit(
    action: string | ITrackingHitSchema,
    button?: string,
    name: string = "n/a",
    code: string = "n/a",
    extraParams: { [key: string]: any } = {}
  ) {
    const layer: ITrackingHitSchema = typeof action == "string" ? {
      ...extraParams,
      hitAction: action.trim().toLowerCase().replace(/ /g, "_"),
      hitButton: button,
      name: name,
      pageTypeCode: this._formatPageType(code)
    } : {
      ...action,
      hitAction: action.hitAction.trim().toLowerCase().replace(/ /g, "_"),
      pageTypeCode: this._formatPageType(action.pageTypeCode)
    };
    this._dataLayer.push({ ...layer, event: "hit", });
    if (!this._userConsentToTrack) return;
    this._pushToAppcues(layer);
  }

  /**
   * Formats page type.
   * @returns if matches a case will return abbreviation, else returns the name.
   */
  private _formatPageType(name: string): string {
    switch (name.toLowerCase().trim()) {
      case 'flights to region': return "TRE";
      case 'flights to country': return "TCO";
      case 'flights to city': return "TCI";
      case 'flights from city': return "FCI";
      case 'flights from country': return "FCO";
      case 'flights from region': return "FRE";
      case 'flights from city to city': return "CICI";
      case 'flights from city to country': return "CICO";
      case 'flights from country to country': return "COCO";
      case 'flights from country to city': return "COCI";
      case 'externalized': return "EXT";
      case 'homepage': return "HP";
      default: return name.trim();
    }
  }

  /**
   * This function tracks a hit event with Appcues SDK.
   */
  private _pushToAppcues(schema: ITrackingHitSchema): void {
    const event = `${schema.hitAction} - ${this._cmsSubSection || this._pageName}`;
    Appcues.track(event, {
      hitButton: schema.hitButton,
      item: schema.name,
      section: this._cmsSection,
      currentAirline: this._cmsProvider,
      currentLanguage: this._cmsLanguageIsoCode,
    });
  }
}
